/**
 * PillarAlertBubble — Iter. 406.fr.73
 * ─────────────────────────────────────
 * Discreet informational bubble rendered inline in the Dashboard, between
 * the pilot-profile pill and the mission tracker (Yuri spec 2026-07-15).
 *
 * Replaces the top-fixed "URGENCE TACTIQUE" banner that had two problems:
 *   1. The "AGIR" button silently logged +30 g P (or +250 ml water) to
 *      the user's day — which is fake data, not a real intake. The user
 *      wants an alert that ONLY warns, never mutates macros.
 *   2. The banner was too loud (full-width, red/amber glow, top of screen).
 *
 * The new bubble:
 *   • Is a small pill inside the Dashboard flow
 *   • Shows which pillar (Protéines / Eau / Pas / Entraînement / Calories /
 *     Sommeil / Mindset) is lagging — picks the most-at-risk one
 *   • Tapping opens the corresponding page (NO auto-log)
 *   • Dismissible per pillar per day
 *   • Only appears after 13h local so morning users are never nagged
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronRight, X } from 'lucide-react';
import { useAuth, useLanguage, api } from '../context/AppContext';
import { getLocalDateString } from '../utils/dateUtils';

const DISMISS_KEY = 'yumix_pillar_alert_dismissed';
const POLL_MS = 60_000;
const ALERT_HOUR_MIN = 13;    // no alerts before 13h local (Yuri)
const SCORE_RISK_THRESHOLD = 80; // total score under which we surface

const PILLAR_INFO = {
  hydration: { fr: 'Eau', en: 'Water',    emoji: '💧', route: '/hydration' },
  protein:   { fr: 'Protéines', en: 'Protein', emoji: '🥩', route: '/meals' },
  calories:  { fr: 'Calories', en: 'Calories', emoji: '🔥', route: '/meals' },
  activity:  { fr: 'Activité', en: 'Activity', emoji: '🏃', route: '/activities' },
  sleep:     { fr: 'Sommeil', en: 'Sleep',   emoji: '🌙', route: '/sleep' },
  mindset:   { fr: 'Mindset', en: 'Mindset', emoji: '🧠', route: '/mindset' },
  steps:     { fr: 'Pas', en: 'Steps',       emoji: '👟', route: '/steps-ranking' },
  fasting:   { fr: 'Jeûne', en: 'Fasting',   emoji: '⏱️', route: '/fasting' },
};

const isDismissed = (userId, pillarKey) => {
  try {
    return localStorage.getItem(`${DISMISS_KEY}_${userId}_${pillarKey}_${getLocalDateString()}`) === '1';
  } catch { return false; }
};
const markDismissed = (userId, pillarKey) => {
  try { localStorage.setItem(`${DISMISS_KEY}_${userId}_${pillarKey}_${getLocalDateString()}`, '1'); }
  catch { /* noop */ }
};

const PillarAlertBubble = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();
  const userId = user?.user_id;

  const [pillarKey, setPillarKey] = useState(null);
  const [hidden, setHidden] = useState(false);

  const evaluate = useCallback((data) => {
    if (!data || !data.pillars) return null;
    const now = new Date();
    if (now.getHours() < ALERT_HOUR_MIN) return null;

    const score = data.score || 0;
    // If the day is already going well, don't nag.
    if (score >= SCORE_RISK_THRESHOLD) return null;

    // Pick the highest-weight pillar that's still not hit today.
    const missed = Object.entries(data.pillars)
      .filter(([key, p]) => !(key === 'fasting' && p && p.has_protocol === false))
      .filter(([, p]) => !p.hit)
      .sort((a, b) => (b[1].max || 0) - (a[1].max || 0));
    if (missed.length === 0) return null;
    return missed[0][0];
  }, []);

  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const fetchOnce = () => api.get('/discipline/today')
      .then((res) => {
        if (!alive) return;
        const nextKey = evaluate(res.data);
        setPillarKey(nextKey);
      })
      .catch(() => {});
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [userId, evaluate]);

  if (!userId || !pillarKey || hidden) return null;
  if (isDismissed(userId, pillarKey)) return null;

  const info = PILLAR_INFO[pillarKey];
  if (!info) return null;
  const label = fr ? info.fr : info.en;

  return (
    <button
      type="button"
      onClick={() => navigate(info.route)}
      data-testid="pillar-alert-bubble"
      data-pillar={pillarKey}
      className="group w-full mb-3 flex items-center gap-2 rounded-full px-3 py-1.5 ring-1 ring-amber-400/40 bg-amber-500/10 hover:bg-amber-500/15 hover:ring-amber-300/60 transition-all text-left"
    >
      <AlertTriangle className="w-3.5 h-3.5 text-amber-300 shrink-0" />
      <span className="text-[11px] font-heading uppercase tracking-widest text-amber-200 shrink-0">
        {fr ? 'Attention' : 'Heads up'}
      </span>
      <span className="text-xs text-foreground truncate flex-1 min-w-0" data-testid="pillar-alert-message">
        <span aria-hidden="true" className="mr-1">{info.emoji}</span>
        {fr
          ? `Ton voyant ${label} est en retard — pense à y jeter un œil.`
          : `Your ${label} dot is lagging — worth a quick check.`}
      </span>
      <ChevronRight className="w-3.5 h-3.5 text-amber-300 shrink-0 opacity-70 group-hover:translate-x-0.5 transition-transform" />
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          markDismissed(userId, pillarKey);
          setHidden(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.stopPropagation();
            markDismissed(userId, pillarKey);
            setHidden(true);
          }
        }}
        aria-label={fr ? 'Ignorer' : 'Dismiss'}
        data-testid="pillar-alert-dismiss"
        className="shrink-0 w-5 h-5 rounded-full hover:bg-amber-500/20 flex items-center justify-center transition-colors"
      >
        <X className="w-3 h-3 text-amber-200" />
      </span>
    </button>
  );
};

export default PillarAlertBubble;
